/**
 * DealActivityComposer.tsx — the deal-scoped activity composer (2026-07-21 UX
 * pass: HubSpot-grade logging on ANY deal).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: CRM pipeline.
 *
 * WHY: the old composer was bound to the deal's OPEN callback ticket, so a
 * deal without one (a fresh widget lead, a terminal deal) had nowhere to log
 * anything. Clinic-service now accepts deal-scoped entries directly —
 * POST /v1/clinic/deals/:id/activities (contract PINNED 2026-07-21):
 *   { kind: 'note'|'call_attempt'|'email', body: string 1..2000, outcome? }
 * with `outcome` REQUIRED iff kind='call_attempt' — so the composer renders
 * unconditionally; new entries land on the same MERGED feed the drawer
 * refetches on success. Three modes mirror the retired ticket composer's
 * grammar: Note is the Jira-grade NoteEditor (UX Wave 4 §4 — toolbar with
 * link + FILE ATTACHMENTS via the deal-scoped signed-upload grant, refs
 * riding the POST as `attachmentRefs` max 5); Log call adds the outcome
 * Select; Log email is plain text. `body` is REQUIRED for every kind (the
 * pinned contract — unlike the old optional ticket note), so submit stays
 * disabled until there is one. A failed POST renders inline and the form
 * KEEPS its values — resubmitting IS the retry (TA-06: never a silent loss).
 */
import { useState, type FormEvent } from 'react';
import { grantDealAttachmentUpload, postDealActivity } from '../api/deals';
import type { CallAttemptOutcome, CallbackActivityKind } from '../api/types';
import { NoteEditor } from '../admin/callbacks/NoteEditor';
import { CALL_OUTCOME_OPTIONS, compactButtonStyle, segmentStyle } from '../admin/callbacks/shared';
import { Button, ErrorLine, Field, Help, Select, TextArea } from '../ui/kit';
import { REQUIRED_ROUND_ATTEMPTS } from './stages';

const KIND_SEGMENTS: Array<{ value: CallbackActivityKind; label: string }> = [
  { value: 'note', label: 'Note' },
  { value: 'call_attempt', label: 'Log call' },
  { value: 'email', label: 'Log email' },
];

export function DealActivityComposer({
  dealId,
  nextAttempt = null,
  onLogged,
}: {
  dealId: string;
  /**
   * §9 — the round position the NEXT logged call lands on (SERVER truth: the
   * drawer derives it from the detail read's attemptsThisRound, never a
   * client-side count). Null = no active call round (or an older server) —
   * the Log-call mode then shows no attempt prompt.
   */
  nextAttempt?: number | null;
  onLogged: () => void;
}) {
  const [kind, setKind] = useState<CallbackActivityKind>('note');
  return (
    <div>
      <div role="group" aria-label="Log" style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: 'var(--gps-s-2)' }}>
        {KIND_SEGMENTS.map((segment) => (
          <button
            key={segment.value}
            type="button"
            aria-pressed={kind === segment.value}
            onClick={() => setKind(segment.value)}
            style={segmentStyle(kind === segment.value)}
          >
            {segment.label}
          </button>
        ))}
      </div>
      {kind === 'note' ? (
        // Wave 4 §4: the note mode is the Jira-grade editor — toolbar with
        // link, plus file attachments uploaded via the deal-scoped grant; the
        // completed refs ride the SAME activity POST as `attachmentRefs`.
        <NoteEditor
          formLabel="Add a note"
          textboxLabel="New note text"
          submitLabel="Add note"
          attachments={{ requestGrant: (contentType) => grantDealAttachmentUpload(dealId, contentType) }}
          onSubmit={async (html, attachmentRefs) => {
            await postDealActivity(dealId, {
              kind: 'note',
              body: html,
              ...(attachmentRefs.length > 0 ? { attachmentRefs } : {}),
            });
            onLogged();
          }}
        />
      ) : kind === 'call_attempt' ? (
        <CallForm dealId={dealId} nextAttempt={nextAttempt} onLogged={onLogged} />
      ) : (
        <EmailForm dealId={dealId} onLogged={onLogged} />
      )}
    </div>
  );
}

/** Log call — the outcome Select lives ONLY here (outcome iff call_attempt). */
function CallForm({
  dealId,
  nextAttempt,
  onLogged,
}: {
  dealId: string;
  nextAttempt: number | null;
  onLogged: () => void;
}) {
  const [outcome, setOutcome] = useState<CallAttemptOutcome>('answered');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (busy || body.trim().length === 0) return;
    setBusy(true);
    setError(null);
    try {
      await postDealActivity(dealId, { kind: 'call_attempt', outcome, body: body.trim() });
      setBody('');
      onLogged();
    } catch {
      // TA-06: the values stay put — resubmitting IS the retry.
      setError('Sorry — we could not log this call. Try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} aria-label="Log a call">
      {/* §9 — the server-counted round position this log will take. Within
          the limit it reads as the CTO wording; past it the round is spent,
          so the prompt points at the handover instead of "attempt 4 of 3". */}
      {nextAttempt !== null ? (
        <Help style={{ margin: '0 0 10px' }}>
          {nextAttempt <= REQUIRED_ROUND_ATTEMPTS
            ? `This will record attempt ${nextAttempt} of ${REQUIRED_ROUND_ATTEMPTS} for this round.`
            : `This round already has its ${REQUIRED_ROUND_ATTEMPTS} attempts — the next step is a handover.`}
        </Help>
      ) : null}
      <Field label="Outcome" htmlFor="deal-call-outcome">
        <Select id="deal-call-outcome" value={outcome} onChange={(e) => setOutcome(e.target.value as CallAttemptOutcome)}>
          {CALL_OUTCOME_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
      </Field>
      <Field label="What happened" htmlFor="deal-call-body">
        <TextArea id="deal-call-body" maxLength={2000} value={body} onChange={(e) => setBody(e.target.value)} />
      </Field>
      {error ? <ErrorLine>{error}</ErrorLine> : null}
      <Button type="submit" variant="primary" disabled={busy || body.trim().length === 0} style={compactButtonStyle}>
        {busy ? 'Saving…' : 'Log call'}
      </Button>
    </form>
  );
}

/** Log email — "what was sent", plain text. */
function EmailForm({ dealId, onLogged }: { dealId: string; onLogged: () => void }) {
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (busy || body.trim().length === 0) return;
    setBusy(true);
    setError(null);
    try {
      await postDealActivity(dealId, { kind: 'email', body: body.trim() });
      setBody('');
      onLogged();
    } catch {
      // TA-06: the values stay put — resubmitting IS the retry.
      setError('Sorry — we could not log this email. Try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} aria-label="Log an email">
      <Field label="What was sent" htmlFor="deal-email-body">
        <TextArea id="deal-email-body" maxLength={2000} value={body} onChange={(e) => setBody(e.target.value)} />
      </Field>
      {error ? <ErrorLine>{error}</ErrorLine> : null}
      <Button type="submit" variant="primary" disabled={busy || body.trim().length === 0} style={compactButtonStyle}>
        {busy ? 'Saving…' : 'Log email'}
      </Button>
    </form>
  );
}
